import { useState } from 'react';
import { formatCurrency, formatDate } from '@/utils/format';
import type { OrderWithDetails } from '@/types/common';
import Modal from '@/components/common/Modal';

interface PaidOrderDetailsModalProps {
  order: OrderWithDetails;
  onClose: () => void;
  onDelete?: (orderId: string) => Promise<void>;
}

export default function PaidOrderDetailsModal({ order, onClose, onDelete }: PaidOrderDetailsModalProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // Add default values for nullable fields
  const tipAmount = order.tip_amount || 0;
  const customAmount = order.custom_amount || 0;
  const subtotal = order.total;

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(order.id);
      setIsDeleteModalOpen(false);
      onClose();
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 transition-opacity bg-black/50 backdrop-blur-sm" 
            onClick={onClose}
          />

          {/* Modal */}
          <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white/80 dark:bg-dark-bg-light/80 backdrop-blur-md shadow-xl rounded-2xl border border-white/20 dark:border-white/10">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200/80 dark:border-dark-border/80">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100/80 dark:bg-green-900/30 rounded-full">
                    <i className="fas fa-check-circle text-green-600 dark:text-green-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">
                      Paid Order
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
                      {formatDate(order.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {onDelete && (
                    <button
                      onClick={() => setIsDeleteModalOpen(true)}
                      className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2"
                      title="Delete Order"
                    >
                      <i className="fas fa-trash-alt" />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="space-y-4">
                {/* Order Info */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-dark-text-secondary">
                    Order #{order.id.slice(0, 8)}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200">
                    Table #{order.table?.number}
                  </span>
                </div>

                {/* Items List */}
                <div className="mt-4 space-y-3">
                  {order.items.map((item) => (
                    <div 
                      key={item.id}
                      className="flex justify-between items-center p-3 rounded-lg bg-white/50 dark:bg-dark-bg/50 border border-gray-200/50 dark:border-dark-border/50"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                          {item.menu_item.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-dark-text-secondary">
                          {formatCurrency(item.menu_item.price)} × {item.quantity}
                        </p>
                      </div>
                      <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                        {formatCurrency(item.menu_item.price * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Total Section */}
                <div className="mt-6 pt-4 border-t border-gray-200/80 dark:border-dark-border/80 space-y-3">
                  {/* Subtotal */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 dark:text-dark-text-secondary">
                      Subtotal
                    </span>
                    <span className="text-gray-900 dark:text-dark-text-primary">
                      {formatCurrency(subtotal)}
                    </span>
                  </div>

                  {/* Tip Amount - Only show if exists */}
                  {tipAmount > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-yellow-600 dark:text-yellow-400">
                        Tip
                      </span>
                      <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                        + {formatCurrency(tipAmount)}
                      </span>
                    </div>
                  )}

                  {/* Custom Amount - Only show if exists */}
                  {customAmount > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-purple-600 dark:text-purple-400">
                        Special
                      </span>
                      <span className="text-purple-600 dark:text-purple-400 font-medium">
                        + {formatCurrency(customAmount)}
                      </span>
                    </div>
                  )}

                  {/* Total */}
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200/50 dark:border-dark-border/50">
                    <span className="text-base font-medium text-gray-900 dark:text-dark-text-primary">
                      Total Paid
                    </span>
                    <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                      {formatCurrency(order.total + tipAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Order"
        actions={
          <>
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:text-dark-text-primary dark:bg-dark-bg dark:hover:bg-dark-bg-light rounded-lg border border-gray-300 dark:border-dark-border"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-dark-text-secondary">
          Are you sure you want to delete this order? This action cannot be undone.
        </p>
      </Modal>
    </>
  );
} 